import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { rememberAssistantRoute } from '../utils/assistantContext';
import { isAssistantRagEnabled, isAssistantTestPublicEnabled } from '../utils/featureFlags';

function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const menuSections = [
    {
      title: 'Principal',
      items: [
        { path: '/administrativo/estadisticas', icon: 'query_stats', label: 'Estadísticas' },
        { path: '/administrativo/clientes', icon: 'group', label: 'Clientes', fill: true },
        { path: '/administrativo/auditoria', icon: 'admin_panel_settings', label: 'Auditoría' },
        ...(isAssistantRagEnabled
          ? [{ path: isAssistantTestPublicEnabled ? '/asistente-test' : '/administrativo/asistente', icon: 'forum', label: 'Asistente IA' }]
          : []),
      ]
    },
    {
      title: 'Facturación',
      items: [
        { path: '/administrativo/mediciones', icon: 'speed', label: 'Mediciones' },
        { path: '/administrativo/generar-boletas', icon: 'receipt_long', label: 'Generar Boletas' },
        { path: '/administrativo/boletas-adeudadas', icon: 'receipt_long', label: 'Boletas Adeudadas', fill: true },
        { path: '/administrativo/cargos-manuales', icon: 'add_circle', label: 'Cargos Manuales' },
        { path: '/administrativo/control-pagos', icon: 'fact_check', label: 'Control de Pagos', fill: true },
        { path: '/administrativo/restricciones-pago', icon: 'rule_settings', label: 'Restricciones de Pago' },
        { path: '/administrativo/cobros-efectivo', icon: 'point_of_sale', label: 'Cobros en Efectivo' },
        { path: '/administrativo/estados-servicio', icon: 'monitor_heart', label: 'Estados de Servicio' },
        { path: '/administrativo/planes-pago', icon: 'account_balance_wallet', label: 'Planes de Pago' },
        { path: '/administrativo/planilla-consumo', icon: 'table_view', label: 'Planilla Consumo' },
        { path: '/administrativo/planilla-colectora', icon: 'description', label: 'Planilla Colectora' },
        { path: '/administrativo/planilla-cobranza-zona', icon: 'assignment', label: 'Planilla Cobranza Zona' },
      ]
    },
    {
      title: 'Configuración',
      items: [
        { path: '/administrativo/tarifario', icon: 'payments', label: 'Tarifario' },
        { path: '/administrativo/zonas', icon: 'map', label: 'Zonas' },
        { path: '/administrativo/usuarios-sistema', icon: 'manage_accounts', label: 'Usuarios Sistema', fill: true },
      ]
    }
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  useEffect(() => {
    rememberAssistantRoute(location.pathname);
  }, [location.pathname]);

  return (
    <aside className="flex h-screen w-64 shrink-0 flex-col bg-white shadow-sm dark:border-r dark:border-gray-700 dark:bg-background-dark">
      <div className="flex items-center justify-center h-20 border-b dark:border-gray-700">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-primary text-3xl">water_drop</span>
          <h1 className="text-xl font-bold text-[#111618] dark:text-white">Agua André</h1>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-4">
        <nav className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-1">
          {menuSections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2 px-3">
                {section.title}
              </p>
              <div className="flex flex-col gap-1">
                {section.items.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => {
                      if (isAssistantTestPublicEnabled && item.path === '/asistente-test') {
                        window.open(item.path, 'agua-andre-chat-test', 'popup,width=1280,height=900');
                      } else {
                        navigate(item.path);
                      }
                    }}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${isActive(item.path) || location.pathname.startsWith(item.path + '/')
                      ? 'bg-primary/20 text-primary dark:bg-primary/30'
                      : 'text-[#617c89] dark:text-gray-400 hover:bg-primary/10 hover:text-primary'
                      }`}
                  >
                    <span className={`material-symbols-outlined ${item.fill && (isActive(item.path) || location.pathname.startsWith(item.path + '/')) ? 'fill' : ''}`}>
                      {item.icon}
                    </span>
                    <p className="text-sm font-medium leading-normal">{item.label}</p>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-1 border-t pt-4 dark:border-gray-700">
            <div className="flex gap-3 items-center p-2">
              <div className="bg-primary/20 flex items-center justify-center rounded-full size-10">
                <span className="material-symbols-outlined text-primary">person</span>
              </div>
              <div className="flex flex-col">
                <h2 className="text-[#111618] dark:text-white text-base font-medium leading-normal">
                  {user?.nombre || 'Admin'}
                </h2>
                <p className="text-[#617c89] dark:text-gray-400 text-sm font-normal leading-normal capitalize">
                  {user?.rol || 'Administrativo'}
                </p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2 text-[#617c89] dark:text-gray-400 hover:bg-primary/10 hover:text-primary rounded-lg transition-colors"
            >
              <span className="material-symbols-outlined">logout</span>
              <p className="text-sm font-medium leading-normal">Cerrar Sesión</p>
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
